import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { cloudSetupMessage, supabase } from '../supabase.js';
import { SignInGate } from '../SignIn.jsx';
import {
  createSavedSearch, deleteSavedSearch, describeSearch, distanceOptions, formatDistance, geocodePostcode, listSavedJobs,
  listSavedSearches, loadSavedKeys, savedKindLabels, setSearchAlerts, toggleSavedJob,
} from './saved.js';

const jobLink = (job) => job.kind === 'private_tender' ? '/tenders/private' : job.kind === 'commercial' ? '/commercial/jobs' : '/supplier/opportunities';
const emptySearch = { name: '', kinds: ['home', 'commercial', 'private_tender'], trade: '', keywords: '', postcode: '', maxMiles: '' };

/** Saved jobs and saved searches (route: /saved). */
export function SavedPage() {
  const { user, loading } = useAuth();
  if (loading) return <div className="content-wrap"><div className="feed-skeleton"><i/><i/></div></div>;
  if (!user) return <SignInGate next="/saved"/>;
  if (!supabase) return <div className="content-wrap account-page"><div className="cloud-notice" role="status">{cloudSetupMessage()}</div></div>;
  return <SavedContent key={user.id} userId={user.id}/>;
}

function SavedContent({ userId }) {
  const [jobs, setJobs] = useState([]);
  const [searches, setSearches] = useState([]);
  const [base, setBase] = useState(null);
  const [postcode, setPostcode] = useState('');
  const [form, setForm] = useState(emptySearch);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [savedJobs, savedSearches] = await Promise.all([listSavedJobs(supabase, base), listSavedSearches(supabase)]);
      setJobs(savedJobs); setSearches(savedSearches); setError('');
    } catch (cause) { setError(cause.message); }
  }, [base]);
  useEffect(() => { void load(); void loadSavedKeys(supabase, userId); }, [load, userId]);

  const run = async (action, message) => {
    setBusy(true); setError(''); setNotice('');
    try { await action(); if (message) setNotice(message); await load(); }
    catch (cause) { setError(cause.message); }
    finally { setBusy(false); }
  };
  const showDistances = (event) => { event.preventDefault(); void run(async () => setBase(postcode.trim() ? await geocodePostcode(postcode) : null)); };
  const saveSearch = (event) => {
    event.preventDefault();
    void run(async () => {
      const point = form.postcode.trim() ? await geocodePostcode(form.postcode) : null;
      await createSavedSearch(supabase, { ...form, base: point, maxMiles: form.maxMiles ? Number(form.maxMiles) : null });
      setForm(emptySearch);
    }, "Search saved. We'll let you know when new jobs match.");
  };
  const toggleKind = (kind) => setForm((current) => ({ ...current, kinds: current.kinds.includes(kind) ? current.kinds.filter((k) => k !== kind) : [...current.kinds, kind] }));

  return <div className="content-wrap coordinated-page saved-page">
    <header className="coord-page-heading"><div><span className="account-kicker">SAVED</span><h1>Saved jobs and searches</h1><p>Keep track of jobs and get alerts when new work matches your searches.</p></div></header>
    {error && <p className="account-form-error" role="alert">{error}</p>}
    {notice && <p className="account-save-status" role="status">{notice}</p>}
    <div className="coord-two-column">
      <section className="account-content-panel" aria-label="Saved jobs">
        <div className="account-panel-heading"><div><span className="account-kicker">JOBS</span><h2>Saved jobs</h2></div><span>{jobs.length}</span></div>
        <form className="coord-form" onSubmit={showDistances}>
          <label>Your postcode, for rough distances<input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="LS6 or LS6 2AB"/></label>
          <small>We only use the first part, like LS6.</small>
          <button className="button button-secondary" disabled={busy}>Show distances</button>
        </form>
        {jobs.length ? <div className="coord-owned-list">{jobs.map((job) => <article key={`${job.kind}:${job.job_id}`}>
          {job.available ? <div>
            <span className="account-kicker">{savedKindLabels[job.kind]}{job.category ? ` · ${job.category}` : ''}</span>
            <strong>{job.title}</strong>
            <p>{[job.location, formatDistance(job.approx_miles), job.response_deadline ? `Respond by ${new Date(`${job.response_deadline}T00:00:00`).toLocaleDateString('en-GB')}` : ''].filter(Boolean).join(' · ')}</p>
            <Link className="text-link" to={jobLink(job)}>Find it in the listings</Link>
          </div> : <div>
            <span className="account-kicker">{savedKindLabels[job.kind]}</span>
            <strong>No longer available</strong>
            <p>This job has closed or been removed.</p>
          </div>}
          <div className="coord-inline-actions"><button className="button button-secondary" disabled={busy} onClick={() => void run(() => toggleSavedJob(supabase, job.kind, job.job_id))}>Remove</button></div>
        </article>)}</div> : <div className="account-empty"><strong>No saved jobs yet</strong><p>Use Save on a listing to keep it here.</p></div>}
      </section>
      <section className="account-content-panel" aria-label="Saved searches">
        <div className="account-panel-heading"><div><span className="account-kicker">SEARCHES</span><h2>Saved searches</h2></div><span>{searches.length}</span></div>
        {searches.length ? <div className="coord-owned-list">{searches.map((search) => <article key={search.id}>
          <div><strong>{search.name}</strong><p>{describeSearch(search)}</p>{search.organization_id && <small>Shared with your company</small>}</div>
          <div className="coord-inline-actions">
            <label><input type="checkbox" checked={search.alerts_enabled} disabled={busy} onChange={(e) => void run(() => setSearchAlerts(supabase, search.id, e.target.checked))}/> Alerts</label>
            <button className="button button-secondary" disabled={busy} onClick={() => { if (window.confirm(`Delete "${search.name}"?`)) void run(() => deleteSavedSearch(supabase, search.id)); }}>Delete</button>
          </div>
        </article>)}</div> : <div className="account-empty"><strong>No saved searches yet</strong><p>Save a search below to get alerts for new jobs.</p></div>}
        <form className="coord-form" onSubmit={saveSearch}>
          <span className="account-kicker">NEW SAVED SEARCH</span>
          <label>Name<input required maxLength="80" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="For example, fencing near Leeds"/></label>
          <fieldset><legend>Types of job</legend>{Object.entries(savedKindLabels).map(([kind, label]) => <label key={kind}><input type="checkbox" checked={form.kinds.includes(kind)} onChange={() => toggleKind(kind)}/> {label}</label>)}</fieldset>
          <label>Trade (optional)<input value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} placeholder="For example, Plumbing"/></label>
          <label>Words to look for (optional)<input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}/></label>
          <label>Postcode (optional)<input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="LS6 or LS6 2AB"/></label>
          <label>Distance<select value={form.maxMiles} onChange={(e) => setForm({ ...form, maxMiles: e.target.value })}><option value="">Any distance</option>{distanceOptions.map((miles) => <option key={miles} value={miles}>Within {miles} miles</option>)}</select></label>
          <button className="button button-blue" disabled={busy}>Save search</button>
        </form>
      </section>
    </div>
  </div>;
}

export default SavedPage;
